import { Link } from "react-router-dom";
import { ArrowRight, Leaf } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  Avatar, HeroActions, HeroCopySkeleton, HeroTitle, Photo, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Big decorative leaf used as a background accent. */
export function LeafShape({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden fill="currentColor">
      <path d="M20 180C20 90 80 20 180 20C180 110 120 180 20 180Z" />
      <path d="M20 180C60 120 100 80 150 50" stroke="hsl(var(--background))" strokeOpacity="0.55" strokeWidth="3" fill="none" />
    </svg>
  );
}

/** Fresh & Natural: arch-masked photo, leafy accents, soft pills. */
export function FreshHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="v-fresh-hero relative -mt-20 overflow-hidden pb-20 pt-32 md:pb-28 md:pt-44" aria-busy={!m.ready}>
      <LeafShape className="absolute -left-16 top-24 h-72 w-72 -rotate-12 text-primary/[0.07] md:h-[26rem] md:w-[26rem]" />
      <LeafShape className="absolute -right-10 bottom-0 h-56 w-56 rotate-[160deg] text-accent/[0.12]" />
      <div className="container relative mx-auto px-5 md:px-6">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            {m.ready ? (
              <>
                <p className="tp-seq inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary" style={seq(0)}>
                  <Leaf className="h-4 w-4" aria-hidden /> {m.eyebrow}
                </p>
                <HeroTitle m={m} edit={edit} className="tp-display tp-display--md tp-seq mt-6" />
                <div className="tp-lead tp-seq mt-6 max-w-[34rem]" style={seq(2)}>{edit?.subtitle ?? <p>{m.subtitle}</p>}</div>
                <div className="tp-seq mt-9" style={seq(3)}><HeroActions m={m} edit={edit} /></div>
                <ul className="tp-seq mt-9 flex flex-wrap gap-2.5" style={seq(4)}>
                  {m.trust.slice(0, 3).map(({ key, Icon, title }) => (
                    <li key={key} className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-4 py-2 text-sm font-medium text-foreground">
                      <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />{title}
                    </li>
                  ))}
                </ul>
              </>
            ) : <HeroCopySkeleton />}
          </div>
          <div className="relative mx-auto w-full max-w-md lg:col-span-6 lg:max-w-none lg:pl-10">
            <div className="tp-hero-media relative mx-auto aspect-[4/5] w-full max-w-md">
              <div className="v-fresh-arch absolute inset-0 overflow-hidden bg-muted shadow-2xl">
                {m.heroPending ? <Skel className="absolute inset-0 h-full w-full rounded-none" /> : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
              </div>
              <span className="absolute -left-5 top-16 h-24 w-24 rounded-full bg-accent/30 blur-sm" aria-hidden />
              <div className="v-fresh-blob absolute -bottom-6 -right-4 w-36 overflow-hidden border-4 border-background bg-muted shadow-xl sm:-right-10 sm:w-44">
                <div className="relative aspect-square"><Photo src={m.gallery[0]} alt="" loading="lazy" /></div>
              </div>
              <div className="tp-glass tp-hero-float absolute -left-4 bottom-12 flex items-center gap-3 rounded-full px-5 py-3 sm:-left-10">
                {m.reviewsPending ? <Skel className="h-5 w-24 tp-skel-round" /> : (<><span className="tp-numeral text-xl text-foreground">{m.rating.toFixed(1)}</span><StarRow className="text-xs" /></>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FreshTrust({ items }: { items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="relative py-6">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="grid gap-3 rounded-[2rem] bg-primary/[0.08] p-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ key, Icon, title, subtitle }, i) => (
            <li key={key} className="flex items-center gap-3 rounded-[1.5rem] bg-card px-5 py-4" data-reveal style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
              <span className="min-w-0 leading-tight"><span className="block text-sm font-semibold text-foreground">{title}</span><span className="mt-0.5 block text-xs text-muted-foreground">{subtitle}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const ORGANIC = ["rounded-[2.5rem_2.5rem_2.5rem_0.75rem]", "rounded-[2.5rem_0.75rem_2.5rem_2.5rem]", "rounded-[0.75rem_2.5rem_2.5rem_2.5rem]"];

export function FreshServices({ s }: { s: ServicesModel }) {
  return (
    <section className="tp-section relative overflow-hidden">
      <LeafShape className="absolute -right-20 top-10 h-80 w-80 rotate-45 text-primary/[0.06]" />
      <div className="container relative mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="tp-eyebrow" data-reveal>What we do</p>
            <h2 className="tp-h2 mt-5" data-reveal>{renderDisplayText(s.title)}</h2>
            <p className="tp-lead mt-4" data-reveal>{renderDisplayText(s.lead)}</p>
          </div>
          <Link to={s.servicesHref} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground" data-reveal>All services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-14 grid gap-6 md:grid-cols-3" aria-busy="true"><LoadingLabel>Loading services</LoadingLabel>{[0, 1, 2].map((i) => <Skel key={i} className="aspect-[4/5] w-full rounded-[2.5rem]" />)}</div>
        ) : (
          <div className={cn("mt-14 grid gap-6 md:mt-20 md:gap-8", s.items.length === 1 ? "mx-auto max-w-sm" : s.items.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3")}>
            {s.items.map((item, i) => (
              <Link key={item.key} to={s.servicesHref} className={cn("group flex flex-col bg-card p-3 shadow-sm ring-1 ring-primary/10 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl", ORGANIC[i % 3], i % 3 === 1 && "lg:translate-y-10", i % 3 === 2 && "md:translate-y-0")} data-reveal>
                <div className={cn("relative aspect-[4/4.4] overflow-hidden bg-muted", ORGANIC[i % 3])}>
                  <Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" />
                  <span className="absolute left-3 top-3 rounded-full bg-background/90 px-3.5 py-1.5 text-xs font-semibold text-primary">{item.price}</span>
                </div>
                <div className="px-4 pb-4 pt-5">
                  <h3 className="tp-h3">{renderDisplayText(item.title)}</h3>
                  {item.description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
        {s.items.length >= 2 && <div className="hidden lg:block lg:h-10" aria-hidden />}
      </div>
    </section>
  );
}

export function FreshReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  return (
    <section className="tp-section tp-tint relative overflow-hidden" aria-busy={r.pending}>
      <div className="container relative mx-auto px-5 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><Leaf className="h-6 w-6" aria-hidden /></span>
          <h2 className="tp-h2 mt-5" data-reveal>What our customers say</h2>
          {!r.pending && <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-card px-5 py-2 text-sm"><StarRow n={r.average} className="text-sm" /><span className="font-semibold text-foreground">{r.average.toFixed(1)}</span><span className="text-muted-foreground">· {r.countLine}</span></p>}
        </div>
        {r.pending ? (
          <div className="tp-skeleton mt-12 grid gap-5 md:grid-cols-3" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel>{[0, 1, 2].map((i) => <Skel key={i} className="h-60 w-full rounded-[2rem]" />)}</div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {r.items.map((item, i) => (
              <figure key={item.key} className={cn("flex flex-col bg-card p-7 shadow-sm ring-1 ring-primary/10", ORGANIC[i % 3], i % 3 === 1 && "lg:mt-8")} data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                <div className="flex items-center justify-between"><StarRow n={item.rating} className="text-sm" />{item.google ? <GoogleGlyph className="h-5 w-5" /> : <Leaf className="h-5 w-5 text-primary/40" aria-hidden />}</div>
                <blockquote className="mt-4 flex-1"><p className="line-clamp-6 leading-relaxed text-foreground">{item.quote}</p></blockquote>
                <figcaption className="mt-6 flex items-center gap-3"><Avatar item={item} className="h-10 w-10" /><div className="min-w-0"><ReviewerLine item={item} /></div></figcaption>
              </figure>
            ))}
          </div>
        )}
        {!r.pending && <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="mt-12 justify-center" />}
      </div>
    </section>
  );
}
